// Config-driven field uniqueness — the single preflight API call.
//
// The endpoint is deliberately narrow: the client names a MODULE and a FORM
// FIELD, never a collection, a collection field or a tenant id. The server
// resolves the target collection, the stored field and the tenant scope from
// the caller's claims + the module's Form Groups config. That is a security
// boundary — do not widen this payload.
//
// POST (never GET) so the value never lands in a URL, browser history or an
// access log.
//
//   200 -> { status: true,  available: true }
//   409 -> { status: false, available: false, code: 'DUPLICATE_VALUE',
//            message, errors: [{ field, code: 'already_present', message }] }
//
// A 409 is a NORMAL structured outcome here, not a transport failure:
// fetchJsonWithAuth throws on every non-2xx, so it is caught and converted back
// into a plain result object. Anything else (network down, 500, gateway HTML)
// is re-thrown — the caller decides how to fail safe.
import { fetchJsonWithAuth } from './authApi';
import { AUTH_URL } from './apiConfig';

export const DUPLICATE_VALUE_CODE = 'DUPLICATE_VALUE';

export async function checkFieldUnique({
  module,
  field,
  value,
  recordId,
  clientId,
  region,
} = {}) {
  if (!module || !field) {
    throw new Error('module and field are required to check uniqueness');
  }

  // Only the four scope keys the contract allows, and only when they carry a
  // value — an explicit `recordId: undefined` on the Add form must not become a
  // `"recordId": null` the server could read as "exclude nothing / something".
  const body = { field, value: value ?? '' };
  if (recordId) body.recordId = String(recordId);
  if (clientId) body.clientId = String(clientId);
  if (region) body.region = String(region);

  try {
    const json = await fetchJsonWithAuth(
      AUTH_URL,
      `/module/validate-unique?module=${encodeURIComponent(module)}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    );

    // Treat anything that is not an explicit `available: false` as available;
    // an older/partial server response must not invent a duplicate.
    const available = json?.available !== false;
    return {
      available,
      duplicate: !available,
      message: json?.message ?? '',
      errors: Array.isArray(json?.errors) ? json.errors : [],
      response: json,
    };
  } catch (err) {
    if (err?.status === 409) {
      const body409 = err.response ?? err.data ?? {};
      return {
        available: false,
        duplicate: true,
        message: body409?.message ?? err.message ?? '',
        errors: Array.isArray(body409?.errors) ? body409.errors : [],
        response: body409,
      };
    }
    throw err;
  }
}

export default checkFieldUnique;

// ── staged duplicate preflight ───────────────────────────────────────────────
// POST /module/duplicate-check?module=X  body: { payload, excludeId }
//
// Asked before a create, so the user can be offered the EXISTING record instead
// of silently creating a second copy of the same person. Which fields are
// compared, in what order, and what happens on a hit are all module config —
// see models.DuplicateCheckStrategy. POST, not GET: the body carries personal
// data (email, phone) that must not reach a URL or a proxy log.
//
// Fails OPEN: a check that cannot run must never block a legitimate create.
export async function checkDuplicateRecord({ module, payload, excludeId } = {}) {
  if (!module || !payload) return { duplicate: false };
  try {
    const json = await fetchJsonWithAuth(
      AUTH_URL,
      `/module/duplicate-check?module=${encodeURIComponent(module)}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ payload, ...(excludeId ? { excludeId } : {}) }),
      },
    );
    return json?.data ?? json ?? { duplicate: false };
  } catch {
    return { duplicate: false };
  }
}
